import { type ReactNode, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  GridViewIcon,
  MoreHorizontalIcon,
} from "@hugeicons/core-free-icons";
import { railHugeIcon } from "./railAppIcons";
import { cn } from "@/lib/utils";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { getAppItems, type NavigationItem } from "@/lib/config/navigationConfig";
import { useAppAccess } from "@/hooks/useAppAccess";
import { useSupportOnly } from "@/hooks/useSupportOnly";
import { useCurrentUser } from "@/lib/hooks/useCurrentUser";
import { useAuthStore } from "@/lib/store/authStore";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

/** Fraction of the viewport height the visible app tiles may occupy before overflowing into the "…" menu. */
const MAX_HEIGHT_RATIO = 0.6;
/** Gap between tiles (matches the `gap-[7px]` on the list container). */
const TILE_GAP = 7;
/** Approx. height of the "…" more-button tile (47px tile + 3px gap + single-line label). */
const MORE_BUTTON_HEIGHT = 63;

/** Desk-style monochrome icon — reference spec: idle #505A62, white when active */
function getAppIcon(app: NavigationItem, isActive = false): ReactNode {
  const colorClass = isActive ? "text-white" : "text-[#505A62] dark:text-white";
  const hugeIcon = railHugeIcon(app);
  if (hugeIcon) {
    return <HugeiconsIcon icon={hugeIcon} size={20} strokeWidth={1.5} className={colorClass} />;
  }
  const Icon = app.icon;
  return <Icon className={cn("w-5 h-5", colorClass)} strokeWidth={1.5} />;
}

/** How many tiles fit within MAX_HEIGHT_RATIO of the viewport, reserving room for a "…" button when needed. */
function computeVisibleCount(heights: number[]): number {
  if (!heights.length) return heights.length;
  const budget = window.innerHeight * MAX_HEIGHT_RATIO;

  const fitCount = (limit: number) => {
    let used = 0;
    let count = 0;
    for (let i = 0; i < heights.length; i++) {
      const add = heights[i] + (i > 0 ? TILE_GAP : 0);
      if (used + add > limit) break;
      used += add;
      count++;
    }
    return count;
  };

  // Everything fits → show all, no overflow menu.
  if (fitCount(budget) >= heights.length) return heights.length;
  // Otherwise reserve space for the "…" button and cap to at least one tile.
  return Math.max(fitCount(budget - (MORE_BUTTON_HEIGHT + TILE_GAP)), 1);
}

/**
 * Vertical app rail — Desk-style icon-only tiles (9px radius, white ring),
 * soft gray tile highlight on active/hover, name shown below each icon.
 * Caps the visible tiles to ~60% of the viewport; the rest collapse into a
 * "…" button that opens a popover listing the remaining apps.
 */
export function ErpSidebarAppRail() {
  const allApps = getAppItems();
  const { hasAccess, isInstalled } = useAppAccess();
  const supportOnly = useSupportOnly();
  const location = useLocation();

  // Support-only tenants (e.g. heybay) get no app tiles — only the Help Center.
  const apps = supportOnly
    ? []
    : allApps.filter((app) => {
        if (app.appRegistryKey && !isInstalled(app.appRegistryKey)) return false;
        if (app.appAccessKey && !hasAccess(app.appAccessKey)) return false;
        return true;
      });

  const appsKey = apps.map((a) => a.name).join("|");
  const listRef = useRef<HTMLDivElement>(null);
  const heightsRef = useRef<number[]>([]);
  // "measure" renders every tile so we can size them; "ready" renders the capped set.
  const [phase, setPhase] = useState<"measure" | "ready">("measure");
  const [visibleCount, setVisibleCount] = useState(Number.POSITIVE_INFINITY);

  // Re-enter the measure phase whenever the set of accessible apps changes.
  useLayoutEffect(() => {
    setPhase("measure");
    setVisibleCount(Number.POSITIVE_INFINITY);
  }, [appsKey]);

  // In the measure phase all tiles are rendered — capture their heights, then cap.
  useLayoutEffect(() => {
    if (phase !== "measure") return;
    const el = listRef.current;
    if (!el) return;
    const items = Array.from(el.querySelectorAll<HTMLElement>("[data-rail-item]"));
    if (items.length !== apps.length) return; // wait until the full list is painted
    heightsRef.current = items.map((i) => i.getBoundingClientRect().height);
    setVisibleCount(computeVisibleCount(heightsRef.current));
    setPhase("ready");
  });

  // Recompute the cap on viewport resize using the stored (width-stable) tile heights.
  useEffect(() => {
    const onResize = () => {
      if (heightsRef.current.length) setVisibleCount(computeVisibleCount(heightsRef.current));
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const showAll = visibleCount >= apps.length;
  const visibleApps = showAll ? apps : apps.slice(0, visibleCount);
  const overflowApps = showAll ? [] : apps.slice(visibleCount);

  return (
    <TooltipProvider delayDuration={200}>
    <div className="flex flex-col h-full">
      {/* Apps — capped to ~60% of the viewport; scrolls if a tiny screen still overflows,
          so the avatar can stay pinned to the bottom. 11px rail padding, 7px tile rhythm. */}
      <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden scrollbar-none">
        <div ref={listRef} className="flex flex-col w-full p-[11px] gap-[7px]">
          {visibleApps.map((app) => {
            const isActive = app.href ? location.pathname.startsWith(app.href) : false;
            return (
              <RailItem
                key={app.name}
                href={app.href ?? "/"}
                name={app.name}
                label={app.railLabel ?? app.name}
                icon={getAppIcon(app, isActive)}
                isActive={isActive}
              />
            );
          })}
          {overflowApps.length > 0 && <MoreAppsButton apps={overflowApps} />}
        </div>
      </div>

      {/* User avatar — pinned to the bottom of the rail at all times */}
      <div className="pb-4 pt-2 flex justify-center shrink-0 border-t border-[#EDEEEF] dark:border-[#242a30]">
        <RailUserAvatar />
      </div>
    </div>
    </TooltipProvider>
  );
}

function RailItem({
  href,
  name,
  label,
  icon,
  isActive,
}: {
  href: string;
  name: string;
  /** Short label shown in the rail; defaults to `name`. */
  label: string;
  icon: ReactNode;
  isActive: boolean;
}) {
  // Exact reference spec (Desk .nav-btns/.grey): 47px tile, 9px radius,
  // 3px white border; hover #F0F1F2; active = dark #292929 tile with a
  // 2px #2794F0 outline ring and white icon. Menu name shown below the icon.
  //
  // The label is forced to a single line. We reveal the full `name` in a
  // tooltip pill on hover when the label is abbreviated (label !== name) OR
  // actually clipped (scrollWidth > clientWidth). Labels that fit and match
  // the full name get no tooltip (open pinned to false).
  const labelRef = useRef<HTMLSpanElement>(null);
  const [truncated, setTruncated] = useState(false);

  useLayoutEffect(() => {
    const el = labelRef.current;
    if (!el) return;
    const check = () => setTruncated(el.scrollWidth > el.clientWidth + 1);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, [label]);

  const showTip = truncated || label !== name;

  return (
    <Tooltip open={showTip ? undefined : false}>
      <TooltipTrigger asChild>
        <Link to={href} data-rail-item className="flex flex-col items-center gap-[3px]">
          <div
            className={cn(
              "flex items-center justify-center w-full h-[47px] rounded-[9px] border-[3px] border-white dark:border-[#1C2126] transition-colors duration-150",
              isActive
                ? "bg-[#292929] outline outline-2 outline-[#2794F0]"
                : "hover:bg-[#F0F1F2] dark:hover:bg-[#242a30]"
            )}
          >
            {icon}
          </div>
          <span
            ref={labelRef}
            className={cn(
              "w-full text-center text-[10px] leading-[1.15] px-[1px] truncate transition-colors duration-150",
              isActive ? "text-[#292929] dark:text-white font-medium" : "text-[#505A62] dark:text-gray-300"
            )}
          >
            {label}
          </span>
        </Link>
      </TooltipTrigger>
      {/* Portal out to <body> so the pill isn't clipped by the rail's
          overflow-x-hidden scroll container. */}
      <TooltipPrimitive.Portal>
        <TooltipContent side="right" sideOffset={8}>
          {name}
        </TooltipContent>
      </TooltipPrimitive.Portal>
    </Tooltip>
  );
}

/** "…" tile that opens a popover listing the apps that didn't fit in the rail. */
function MoreAppsButton({ apps }: { apps: NavigationItem[] }) {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const anyActive = apps.some((a) => a.href && location.pathname.startsWith(a.href));
  const highlight = open || anyActive;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" title="แอปเพิ่มเติม" className="flex flex-col items-center gap-[3px] w-full">
          <div
            className={cn(
              "flex items-center justify-center w-full h-[47px] rounded-[9px] border-[3px] border-white dark:border-[#1C2126] transition-colors duration-150",
              highlight
                ? "bg-[#292929] outline outline-2 outline-[#2794F0]"
                : "hover:bg-[#F0F1F2] dark:hover:bg-[#242a30]"
            )}
          >
            <HugeiconsIcon
              icon={MoreHorizontalIcon}
              size={20}
              strokeWidth={1.5}
              className={cn(highlight ? "text-white" : "text-[#505A62] dark:text-white")}
            />
          </div>
          <span
            className={cn(
              "w-full text-center text-[10px] leading-[1.15] px-[1px] truncate",
              highlight ? "text-[#292929] dark:text-white font-medium" : "text-[#505A62] dark:text-gray-300"
            )}
          >
            เพิ่มเติม
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent side="right" align="center" sideOffset={10} className="w-56 p-1.5">
        <div className="px-2 py-1.5 text-[11px] font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500">
          แอปอื่นๆ
        </div>
        <div className="flex flex-col">
          {apps.map((app) => {
            const Icon = app.icon;
            const hugeIcon = railHugeIcon(app);
            const isActive = app.href ? location.pathname.startsWith(app.href) : false;
            return (
              <Link
                key={app.name}
                to={app.href ?? "/"}
                onClick={() => setOpen(false)}
                className={cn(
                  "flex items-center gap-2.5 rounded-md px-2 py-1.5 text-sm transition-colors",
                  isActive
                    ? "bg-[#F0F1F2] dark:bg-[#242a30] font-medium text-gray-900 dark:text-white"
                    : "text-gray-700 hover:bg-[#F0F1F2] dark:text-gray-200 dark:hover:bg-[#242a30]"
                )}
              >
                {hugeIcon ? (
                  <HugeiconsIcon
                    icon={hugeIcon}
                    size={16}
                    strokeWidth={1.5}
                    className="shrink-0 text-[#505A62] dark:text-gray-300"
                  />
                ) : (
                  <Icon className="w-4 h-4 shrink-0 text-[#505A62] dark:text-gray-300" strokeWidth={1.5} />
                )}
                <span className="truncate">{app.name}</span>
              </Link>
            );
          })}
        </div>
        <div className="my-1 h-px bg-gray-200/60 dark:bg-gray-700/60" />
        <Link
          to="/all-apps"
          onClick={() => setOpen(false)}
          className="flex items-center gap-2.5 rounded-md px-2 py-1.5 text-sm text-[#2794F0] transition-colors hover:bg-[#F0F1F2] dark:hover:bg-[#242a30]"
        >
          <HugeiconsIcon icon={GridViewIcon} size={16} strokeWidth={1.5} className="shrink-0" />
          <span>ดูแอปทั้งหมด</span>
        </Link>
      </PopoverContent>
    </Popover>
  );
}

function RailUserAvatar() {
  const { data: currentUser } = useCurrentUser();
  const username = useAuthStore((state) => state.username);
  const displayName = currentUser?.full_name || username || "User";

  const getInitials = (name: string): string => {
    const parts = name.split(" ").filter(Boolean);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  };

  return (
    <Link
      to="/settings"
      title={displayName}
      className="flex items-center justify-center w-9 h-9 rounded-full overflow-hidden transition-opacity hover:opacity-80"
    >
      {currentUser?.user_image ? (
        <img
          src={currentUser.user_image}
          alt={displayName}
          className="w-9 h-9 rounded-full object-cover"
        />
      ) : (
        <div className="w-9 h-9 rounded-full bg-gray-400 dark:bg-gray-500 flex items-center justify-center text-white font-medium text-xs">
          {getInitials(displayName)}
        </div>
      )}
    </Link>
  );
}
